(function() {
    'use strict';

    const CMP = require('@edreamsodigeo/frontend-cmp-client');

    /**
     @namespace Odigeo.Utils Holds all the framework reusable util functions
     */
    Odigeo.extendNamespace(Odigeo, 'Utils');
    var Utils = Odigeo.Utils;

    /*
     * Date Format 1.2.3
     * (c) 2007-2009 Steven Levithan <stevenlevithan.com>
     * MIT license

     * Includes enhancements by Scott Trenda <scott.trenda.net>
     * and Kris Kowal <cixar.com/~kris.kowal/>
     *
     * Accepts a date, a mask, or a date and a mask.
     * Returns a formatted version of the given date.
     * The date defaults to the current date/time.
     * The mask defaults to dateFormat.masks.default.
     */
    /**
     * @link http://blog.stevenlevithan.com/archives/date-time-format
     */
    Utils.formatDate = (function() {
        var token = /d{1,4}|m{1,4}|yy(?:yy)?|([HhMsTt])\1?|[LloSZ]|"[^"]*"|'[^']*'/g;
        var timezone = new RegExp('\\b(?:[PMCEA][SDP]T|(?:Pacific|Mountain|Central|Eastern|Atlantic) ' +
                '(?:Standard|Daylight|Prevailing) Time|(?:GMT|UTC)(?:[-+]\\d{4})?)\\b', 'g');
        var timezoneClip = /[^-+\dA-Z]/g;
        var pad = function(val, len) {
            val = String(val);
            len = len || 2;
            while (val.length < len) {
                val = '0' + val;
            }
            return val;
        };

        // Regexes and supporting functions are cached through closure
        return function(date, mask, utc) {
            var dF = Utils.formatDate;

            // You can't provide utc if you skip other args (use the "UTC:" mask prefix)
            if (arguments.length === 1 && Object.prototype.toString.call(date) === '[object String]' && !/\d/.test(date)) {
                mask = date;
                date = undefined;
            }

            // Passing date through Date applies Date.parse, if necessary
            date = date ? Odigeo.Utils.createDateFromString(date) : new Date();
            if (isNaN(date)) {
                throw new SyntaxError('invalid date');
            }

            mask = String(dF.masks[mask] || mask || dF.masks['default']);

            // Allow setting the utc argument via the mask
            if (mask.slice(0, 4) === 'UTC:') {
                mask = mask.slice(4);
                utc = true;
            }

            var _ = utc ? 'getUTC' : 'get';
            var d = date[_ + 'Date']();
            var D = date[_ + 'Day']();
            var m = date[_ + 'Month']();
            var y = date[_ + 'FullYear']();
            var H = date[_ + 'Hours']();
            var M = date[_ + 'Minutes']();
            var s = date[_ + 'Seconds']();
            var L = date[_ + 'Milliseconds']();
            var o = utc ? 0 : date.getTimezoneOffset();
            var flags = {
                d: d,
                dd: pad(d),
                ddd: dF.i18n.dayShortNames[D],
                dddd: dF.i18n.dayNames[D],
                m: m + 1,
                mm: pad(m + 1),
                mmm: dF.i18n.monthShortNames[m],
                mmmm: dF.i18n.monthNames[m],
                yy: String(y).slice(2),
                yyyy: y,
                h: H % 12 || 12,
                hh: pad(H % 12 || 12),
                H: H,
                HH: pad(H),
                M: M,
                MM: pad(M),
                s: s,
                ss: pad(s),
                l: pad(L, 3),
                L: pad(L > 99 ? Math.round(L / 10) : L),
                t: H < 12 ? 'a' : 'p',
                tt: H < 12 ? 'am' : 'pm',
                T: H < 12 ? 'A' : 'P',
                TT: H < 12 ? 'AM' : 'PM',
                Z: utc ? 'UTC' : (String(date).match(timezone) || ['']).pop().replace(timezoneClip, ''),
                o: (o > 0 ? '-' : '+') + pad(Math.floor(Math.abs(o) / 60) * 100 + Math.abs(o) % 60, 4),
                S: ['th', 'st', 'nd', 'rd'][d % 10 > 3 ? 0 : (d % 100 - d % 10 !== 10) * d % 10],
            };

            return mask.replace(token, function($0) {
                return $0 in flags ? flags[$0] : $0.slice(1, $0.length - 1);
            });
        };
    }());

    // Some common format strings
    Utils.formatDate.masks = {
        'default': 'ddd mmm dd yyyy HH:MM:ss',
        'justDay': 'dd',
        'justMonth': 'mm',
        'shortDate': 'm/d/yy',
        'mediumDate': 'mmm d, yyyy',
        'longDate': 'mmmm d, yyyy',
        'fullDate': 'dddd, mmmm d, yyyy',
        'shortTime': 'h:MM TT',
        'mediumTime': 'h:MM:ss TT',
        'longTime': 'h:MM:ss TT Z',
        'isoDate': 'yyyy-mm-dd',
        'isoTime': 'HH:MM:ss',
        'isoDateTime': 'yyyy-mm-dd\'T\'HH:MM:ss',
        'datepicker': 'ddd. d mmmm, yyyy',
        'datepickerExtendedMonth': 'ddd. d mmmm, yyyy',
        'dayMonth': 'mmm d',
        'dayMonthShort': 'dd mmm',
        'extendedDayDayMonth': 'ddd, dd mmm',
        'hourMinutes': 'HH:MM',
        'yearMonth': 'yyyy-mm',
        'dayShortWithTime': 'ddd HH:MM',
        'dayShortWithTimeCommaSeparated': 'ddd, HH:MM',
        'yearMonthDayHoursMinutes': 'yyyy-mm-dd HH:MM',
        'dayMonthTime': 'dd mmmm. HH:MM TT',
        'monthYear': 'mmmm yy',
        'datepickerShortMonth': 'ddd. d mmm, yyyy',
    };

    // Internationalization strings
    Utils.formatDate.i18n = {
        dayNames: null,
        dayShortNames: null,
        monthNames: null,
        monthShortNames: null,
    };

    Utils.parseDate = function(dateString, formatString) {
        var dateSplit;
        if (!formatString) {
            formatString = 'yyyy-mm-dd';
        }
        switch (formatString.trim().toLowerCase()) {
            case 'yyyy-mm-dd':
                dateSplit = dateString.split('-');
                break;
            case 'dd/mm/yyyy':
                dateSplit = dateString.split('/').reverse();
                break;
            case 'dd.mm.yyyy':
                dateSplit = dateString.split('.').reverse();
                break;
            default:
                // if format is unrecognized, we'll generate the default date in javascript
                return new Date(dateString);
        }

        dateSplit = _.map(dateSplit, Number);
        dateSplit[1]--;
        return new Date(Date.UTC.apply({}, dateSplit));
    };

    Utils.getNextDay = function(date) {
        return Odigeo.Utils.getNextNthDay(date, 1);
    };

    Utils.getNextNthDay = function(date, days) {
        try {
            var nextDay = new Date(date);
            nextDay.setDate(nextDay.getDate() + days);
            return date ? Odigeo.Utils.formatDate(nextDay.toISOString(), 'isoDate', true) : '';
        } catch (error) {
            console.error('Utils.getNextNthDay: failed to get next nth day from date.', error);
            return null;
        }
    };

    /**
     * Parses a string containing JSON formatted data into an object.
     * @param {string} text The string to be parsed.
     * @return {Object} An object containing the parsed data, or null if an error has
     * occurred while trying to parse the data.
     */
    Utils.parseJson = function(text) {
        try {
            return JSON.parse(text);
        } catch (error) {
            console.error('Failed to parse JSON data.', error); // eslint-disable-line no-console
            return null;
        }
    };

    Utils.parseNodes = function(text) {
        try {
            return $(text);
        } catch (error) {
            console.error('Failed to parsing nodes.', error); // eslint-disable-line no-console
            return $();
        }
    };

    Utils.formatDateFromString = function(dateString, originFormat, destinationFormat) {
        return Utils.formatDate(Utils.parseDate(dateString, originFormat), destinationFormat);
    };

    // create a valid Date preventing exceptions to weird strings not allowed in IE
    Utils.createDateFromString = function(dateString) {
        var returnDate = new Date(dateString);

        try {
            if (returnDate === 'Invalid Date' && dateString) {
                // '2014-06-18 17:35:28 +0200' or '2014-06-18T17:35:28+0200' or '2014-06-18 17:35:28+02:00'
                var pattern = /([0-9-]+)[ T]([0-9:]+)\s*(\+[0-9][0-9]):?([0-9][0-9])/i;
                // is transformed to '2014-06-18T17:35:28+02:00'
                returnDate = new Date(dateString.replace(pattern, '$1T$2$3:$4'));
            } else if (!dateString) {
                throw new SyntaxError('Invalid Date');
            }
        } catch (error) {
            console.error('Invalid data.', error); // eslint-disable-line no-console
        }

        return returnDate;
    };

    /**
     * Provides the value in milliseconds of the current day at 00:00 UTC
     * @param {array} precision
     * @param {Date} stubDate
     * @return {number}
     */
    var getTodayByArgs = function(precision, stubDate) {
        var now = _.isDate(stubDate) ? stubDate : new Date();
        var args = [];

        precision.forEach(function(functionName) {
            args.push(now[functionName].apply(now));
        }, this);

        return Date.UTC.apply(Date, args);
    };

    Utils.getToday = _.partial(getTodayByArgs, ['getUTCFullYear', 'getUTCMonth', 'getUTCDate']);
    Utils.getTodayExactTimeUTC = _.partial(getTodayByArgs, ['getUTCFullYear', 'getUTCMonth', 'getUTCDate', 'getUTCHours', 'getUTCMinutes']);
    Utils.getTodayExactTimeLocal = _.partial(getTodayByArgs, ['getFullYear', 'getMonth', 'getDate', 'getHours', 'getMinutes']);

    Utils.addMins = function(time, minutes) {
        return time + (minutes * 60 * 1000);
    };

    Utils.addHours = function(time, hours) {
        return this.addMins(time, hours * 60);
    };

    Utils.addDays = function(time, days) {
        return this.addHours(time, days * 24);
    };

    /**
     * Display a human text for elapsed time
     * @see http://momentjs.com/docs/#/displaying/fromnow/
     * @param {number} timestamp
     * @return {String} the human text for elapsed time
     */
    Utils.fromNow = function(timestamp) {
        var diff = new Date().getTime() - timestamp;
        var second = 1000;
        var minute = 60 * second;
        var hour = 60 * minute;
        var day = 24 * hour;
        var month = 30 * day;
        var year = 12 * month;
        var breakPoints = [
            {
                key: 'aFewSecondsAgo',
                limit: 45 * second,
            },
            {
                key: 'aMinuteAgo',
                limit: 90 * second,
            },
            {
                key: 'xMinutesAgo',
                limit: 45 * minute,
                param: minute,
            },
            {
                key: 'anHourAgo',
                limit: 90 * minute,
            },
            {
                key: 'xHoursAgo',
                limit: 21 * hour,
                param: hour,
            },
            {
                key: 'aDayAgo',
                limit: 35 * hour,
            },
            {
                key: 'xDaysAgo',
                limit: 25 * day,
                param: day,
            },
            {
                key: 'aMonthAgo',
                limit: 45 * day,
            },
            {
                key: 'xMonthsAgo',
                limit: 320 * day,
                param: month,
            },
            {
                key: 'aYearAgo',
                limit: 548 * day,
            },
            {
                key: 'xYearsAgo',
                limit: Number.POSITIVE_INFINITY,
                param: year,
            },
        ];
        var breakPoint = _.find(breakPoints, function(breakPoint) {
            return diff < breakPoint.limit;
        });
        var text = Utils.fromNow.i18n[breakPoint.key];
        var value = breakPoint.param && Math.ceil(diff / breakPoint.param);

        return Utils.replaceCmsKeyArgs(text, value);
    };

    Utils.fromNow.i18n = {};

    Utils.InterfaceClient = {
        DESKTOP: 'ONE_FRONT_DESKTOP',
        SMARTPHONE: 'ONE_FRONT_SMARTPHONE',
    };

    Utils.Partition = {
        A: 1,
        B: 2,
        C: 3,
        D: 4,
    };

    Utils.sortTypes = {
        byPrice: {
            value: 'SMART_SORTING',
            trackingLabel: 'price',
        },
        byRecommended: {
            value: 'ODIRATING_SORTING',
            trackingLabel: 'recom',
        },
        byDuration: {
            value: 'DURATION',
            trackingLabel: 'duration',
        },
    };

    var roundDateMinutesAsRequired = function(roundToMin, time) {
        var mins = parseInt(Utils.formatDate(time, 'MM', true), 10);
        var factor = Math.round(mins / roundToMin);
        var minsToAdd = roundToMin * factor - mins;

        return Utils.addMins(time, minsToAdd);
    };

    Utils.roundDateMinutesToQuarterHours = _.partial(roundDateMinutesAsRequired, 15);

    /**
     * Credit card validation makes a validation taking into account the Luhn Algorithm and
     * the known regexes for specified credit card types.
     * @param {string} value - of the credit car to test
     * @param {object} param - list of types to validate with
     * @param {boolean} [param.mastercard]
     * @param {boolean} [param.visa]
     * @param {boolean} [param.amex]
     * @param {boolean} [param.dinersclub]
     * @param {boolean} [param.enroute]
     * @param {boolean} [param.discover]
     * @param {boolean} [param.jcb]
     * @param {boolean} [param.carteblanche]
     * @param {boolean} [param.uatp]
     * @param {boolean} [param.unknown]
     * @param {boolean} [param.all]
     *
     * @return {boolean}
     */
    Utils.isValidCreditCard = function(value, param) {
        var nCheck = 0;
        var nDigit = 0;
        var bEven = false;
        var cDigit;
        var n;
        var numericString = _.isString(value) ? value.replace(/[/\s-]/g, '') : '';
        var numericValue = numericString.length >= 6 ? _.parseInt(numericString.substring(0, 6)) : NaN;

        if (/[^0-9-]+/.test(value)) {
            return false;
        }

        value = value.replace(/\D/g, '');

        // The Luhn Algorithm
        for (n = value.length - 1; n >= 0; n--) {
            cDigit = value.charAt(n);
            nDigit = parseInt(cDigit, 10);

            if (bEven) {
                if ((nDigit *= 2) > 9) {
                    nDigit -= 9;
                }
            }
            nCheck += nDigit;
            bEven = !bEven;
        }

        if ((nCheck % 10) !== 0) {
            return false;
        }

        var validTypes = 0x0000;

        if (param.mastercard) {
            validTypes |= 0x0001;
        }
        if (param.visa) {
            validTypes |= 0x0002;
        }
        if (param.amex) {
            validTypes |= 0x0004;
        }
        if (param.dinersclub) {
            validTypes |= 0x0008;
        }
        if (param.enroute) {
            validTypes |= 0x0010;
        }
        if (param.discover) {
            validTypes |= 0x0020;
        }
        if (param.jcb) {
            validTypes |= 0x0040;
        }
        if (param.carteblanche) {
            validTypes |= 0x0080;
        }
        if (param.unknown) {
            validTypes |= 0x0100;
        }
        if (param.cartebleuenationale) {
            validTypes |= 0x0200;
        }
        if (param.maestro) {
            validTypes |= 0x0400;
        }
        if (param.electron) {
            validTypes |= 0x0800;
        }
        if (param.uatp) {
            validTypes |= 0x1000;
        }
        if (param.all) {
            validTypes = 0x0001 | 0x0002 | 0x0004 | 0x0008 | 0x0010 | 0x0020 | 0x0040 | 0x0080 | 0x0100 | 0x0200 | 0x0400 | 0x0800;
        }
        if (numericValue >= 222100 && numericValue <= 272099) { // mastercard special case
            return value.length === 16;
        }
        if (validTypes & 0x0001 && /^(5[12345]|58[19]|601576|426055|451416)/.test(value)) { // mastercard
            return value.length === 16;
        }
        if (validTypes & 0x0002 && /^(4|528392)/.test(value)) { // visa
            return value.length === 16 || value.length === 13;
        }
        if (validTypes & 0x0004 && /^(3[23457])/.test(value)) { // amex
            return value.length === 15;
        }
        if (validTypes & 0x0008 && /^(3[0-689]|5[45])/.test(value)) { // dinersclub
            return value.length === 14 || value.length === 16;
        }
        if (validTypes & 0x0010 && /^(2(014|149))/.test(value)) { // enroute
            return value.length === 15;
        }
        if (validTypes & 0x0020 && // discover
            /^(6001|601[138]|622(12[6-9]|1[3-9][0-9]|[2-8][0-9]{2}|9[0-1][0-9]|92[0-5]|64[4-9])|6[54])/.test(value)) {
            return value.length === 16;
        }
        if (validTypes & 0x0040 && /^(35|364826)/.test(value)) { // jcb
            return value.length === 16;
        }
        if (validTypes & 0x0040 && /^(2131|1800)/.test(value)) { // jcb
            return value.length === 15;
        }
        if (validTypes & 0x0080 && /^(3(0[0-5]|[68]))/.test(value)) { // carteblanche
            return value.length === 14;
        }
        if (validTypes & 0x0200 && /^(4)/.test(value)) { // cartebleuenationale
            return value.length === 16;
        }
        if (validTypes & 0x0400 && /^(554|5[60789]|6[0-46-9]|65[15]|0604|774|70[876])/.test(value)) { // Maestro
            return value.length >= 12 && value.length <= 19;
        }
        if (validTypes & 0x0800 && /^(4)/.test(value)) { // Visa Electron
            return value.length === 16;
        }
        if (validTypes & 0x1000 && /^(1)/.test(value)) { // uatp
            return value.length === 15;
        }
        if (validTypes & 0x0100) { // unknown
            return true;
        }
        return false;
    };


    // Check if a postal code is valid depending on the country //
    Utils.isValidPostalCode = function(countryCode, postalCode) {
        var postalCodeRegex;
        var multipleCodeRegex;
        var multipleCode;

        multipleCodeRegex = {
            MLT01: ['DE', 'FI', 'DZ', 'BA', 'KH', 'DO', 'HR', 'EG', 'EE', 'GT', 'ID', 'JO', 'KE', 'KW', 'LA', 'LT', 'MY', 'MV', 'MX', 'MA',
                'NP', 'PK', 'PS', 'SA', 'SN', 'LK', 'TH', 'TR', 'UA', 'UY', 'ZM', 'CS', 'YU', 'IQ', 'XK', 'HN', 'PE', 'MN', 'RS'],
            MLT02: ['AU', 'CH', 'AT', 'BE', 'DK', 'NO', 'AZ', 'BD', 'BG', 'CV', 'CY', 'GE', 'HT', 'HU', 'LV', 'LU', 'MK', 'MD', 'NZ', 'PY', 'PH',
                'SI', 'ZA', 'TN', 'VE', 'CK', 'ET', 'GW', 'HM', 'LR', 'NE', 'SJ'],
            MLT03: ['CN', 'SG', 'BY', 'KZ', 'RO', 'RU', 'TJ', 'TM', 'UZ', 'KG', 'EC'],
            MLT04: ['FO', 'IS', 'GN', 'LS', 'MG', 'PG'],
            MLT05: ['SE', 'CZ', 'GR', 'SK'],
        };

        multipleCode = _.keys(multipleCodeRegex);

        postalCodeRegex = {
            ES: /^(0[1-9]|5[0-2]|[0-4][0-9])[0-9]{3}$/,
            IT: /^(0000[1-9])$|^(\d{3}[1-9]\d)$|^(\d{2}[1-9]\d{2})$|^(\d{1}[1-9]\d{3})$|^([1-9]\d{4})$/,
            SO: /^[A-Z]{2}$/,
            IN: /^\d{3}[ ]?\d{3}$/,
            GB: /^GIR[ ]?0AA|((AB|AL|B|BA|BB|BD|BH|BL|BN|BR|BS|BT|CA|CB|CF|CH|CM|CO|CR|CT|CV|CW|DA|DD|DE|DG|DH|DL|DN|DT|DY|E|EC|EH|EN|EX|FK|FY|G|GL|GY|GU|HA|HD|HG|HP|HR|HS|HU|HX|IG|IM|IP|IV|JE|KA|KT|KW|KY|L|LA|LD|LE|LL|LN|LS|LU|M|ME|MK|ML|N|NE|NG|NN|NP|NR|NW|OL|OX|PA|PE|PH|PL|PO|PR|RG|RH|RM|S|SA|SE|SG|SK|SL|SM|SN|SO|SP|SR|SS|ST|SW|SY|TA|TD|TF|TN|TQ|TR|TS|TW|UB|W|WA|WC|WD|WF|WN|WR|WS|WV|YO|ZE)(\d[\dA-Z]?[ ]?\d[ABD-HJLN-UW-Z]{2}))|BFPO[ ]?\d{1,4}$/, // eslint-disable-line max-len
            JE: /^JE\d[\dA-Z]?[ ]?\d[ABD-HJLN-UW-Z]{2}$/,
            GG: /^GY\d[\dA-Z]?[ ]?\d[ABD-HJLN-UW-Z]{2}$/,
            IL: /^\d{7}$/,
            IM: /^IM\d[\dA-Z]?[ ]?\d[ABD-HJLN-UW-Z]{2}$/,
            US: /^\d{5}([ -]\d{4})?$/,
            CA: /^[ABCEGHJKLMNPRSTVXY]\d[ABCEGHJ-NPRSTV-Z][ ]?\d[ABCEGHJ-NPRSTV-Z]\d$/,
            JP: /^\d{3}-\d{4}$/,
            FR: /^(0[1-9]\d{3})$|^([1-8][0-9]\d{3})$|^(9[0-5]\d{3})$/,
            NL: /^\d{4}[ ]?[A-Z]{2}$/,
            BR: /^\d{5}[-]?\d{3}$/,
            PT: /^\d{4}([-]\d{3})?$/,
            AX: /^22\d{3}$/,
            KR: /^\d{3}[-]\d{3}$/,
            TW: /^\d{3}(\d{2})?$/,
            AD: /^AD\d{3}$/,
            AR: /^([A-HJ-NP-Z])?\d{4}([A-Z]{3})?$/,
            AM: /^(37)?\d{4}$/,
            BH: /^((1[0-2]|[2-9])\d{2})?$/,
            BB: /^(BB\d{5})?$/,
            BM: /^[A-Z]{2}[ ]?[A-Z0-9]{2}$/,
            IO: /^BBND 1ZZ$/,
            BN: /^[A-Z]{2}[ ]?\d{4}$/,
            CL: /^\d{7}$/,
            CR: /^\d{4,5}|\d{3}-\d{4}$/,
            GL: /^39\d{2}$/,
            LB: /^(\d{4}([ ]?\d{4})?)?$/,
            LI: /^(948[5-9])|(949[0-7])$/,
            MT: /^[A-Z]{3}[ ]?\d{2,4}$/,
            MU: /^(\d{3}[A-Z]{2}\d{3})?$/,
            MC: /^980\d{2}$/,
            NI: /^((\d{4}-)?\d{3}-\d{3}(-\d{1})?)?$/,
            NG: /^(\d{6})?$/,
            OM: /^(PC )?\d{3}$/,
            PL: /^\d{2}-\d{3}$/,
            PR: /^00[679]\d{2}([ -]\d{4})?$/,
            SM: /^4789\d$/,
            VA: /^00120$/,
            AS: /^96799$/,
            CC: /^6799$/,
            ME: /^8\d{4}$/,
            CX: /^6798$/,
            FK: /^FIQQ 1ZZ$/,
            NF: /^2899$/,
            FM: /^(9694[1-4])([ -]\d{4})?$/,
            GF: /^973([0-8][0-9]|90)$/,
            GP: /^971([0-8][0-9]|90)$/,
            GS: /^SIQQ 1ZZ$/,
            GU: /^969[123]\d([ -]\d{4})?$/,
            MH: /^969[67]\d([ -]\d{4})?$/,
            MP: /^9695[012]([ -]\d{4})?$/,
            MQ: /^9[78]2\d{2}$/,
            NC: /^988\d{2}$/,
            VI: /^008(([0-4]\d)|(5[01]))([ -]\d{4})?$/,
            PF: /^987\d{2}$/,
            PM: /97500$/,
            PN: /^PCRN 1ZZ$/,
            PW: /^96940$/,
            RE: /^974\d{2}$/,
            SH: /^(ASCN|STHL) 1ZZ$/,
            SZ: /^[HLMS]\d{3}$/,
            TC: /^TKCA 1ZZ$/,
            WF: /^986\d{2}$/,
            YT: /^976\d{2}$/,
            MLT01: /^\d{5}$/,
            MLT02: /^\d{4}$/,
            MLT03: /^\d{6}$/,
            MLT04: /^\d{3}$/,
            MLT05: /^\d{3}[ ]?\d{2}$/,
            DEFAULT: /^[- '0-9A-Za-z\u00c0\u00c1\u00c2\u00c3\u00c4\u00c5\u00e0\u00e1\u00e2\u00e3\u00e4\u00e5\u00c6\u00e6\u00df\u00c8\u00c9\u00ca\u00cb\u00e8\u00e9\u00ea\u00eb\u00cc\u00cd\u00ce\u00cf\u0130\u00ec\u00ed\u00ee\u00ef\u0131\u0152\u0153\u00d2\u00d3\u00d4\u00d5\u00d6\u00d8\u00f8\u00f2\u00f3\u00f4\u00f5\u00f6\u00f8\u00d9\u00da\u00db\u00dc\u00f9\u00fa\u00fb\u00fc\u0178\u00dd\u00fd\u00ff\u00c7\u00e7\u00d1\u00f1\u00d0\u00fe\u00de\u00b5\u0160\u0161\u015e\u015f\u011e\u011f\u0192]{1,10}$/, // eslint-disable-line max-len
        };

        if (!_.isUndefined(postalCodeRegex[countryCode])) {
            return postalCodeRegex[countryCode].test(postalCode);
        } else {
            for (var key in multipleCode) {
                if (_.indexOf(multipleCodeRegex[multipleCode[key]], countryCode) > -1) {
                    return postalCodeRegex[multipleCode[key]].test(postalCode);
                }
            }
            return postalCodeRegex['DEFAULT'].test(postalCode);
        }
    };

    /**
     * Validate text with regex
     * @param {String} text
     * @param {Regex} regex
     * @return {boolean}
     */
    Utils.isValidRegex = function(text, regex) {
        return !!text.match(regex);
    };

    /**
     * Validate password format
     * @param {String} pass
     * @param {Boolean} noAcceptLegacyPasswords
     * @return {boolean}
     */
    Utils.isValidPassword = function(pass, noAcceptLegacyPasswords) {
        var regExp = (noAcceptLegacyPasswords === true) ? /^(?=.*\d)(?=.*[a-zA-Z])(?!.*\s).{7,}$/ : /^.*(?=.{7,})(?=.*\d)(?=.*[a-zA-Z]).*$/;
        return Utils.isValidRegex(pass, regExp);
    };

    /**
     * Validate email format
     * @param {String} email
     * @return {boolean}
     */
    Utils.isValidEmail = function(email) {
        return Utils.isValidRegex(email,
            /^[_A-Za-z0-9-]+(\.[_A-Za-z0-9-]+)*@[A-Za-z0-9]+([-]*[A-Za-z0-9])*(\.[A-Za-z0-9]+([-]*[A-Za-z0-9])*)*(\.[A-Za-z]{2,})$/);
    };

    /**
     * Validate cif format
     * @param {String} cif
     * @return {boolean}
     */
    Utils.isValidCif = function(cif) {
        var number;
        var control;
        var controlLetter;
        var controlDigit;
        var n;
        var sum = 0;
        var match = _.isString(cif) ? cif.match(/^([ABCDEFGHJKLMNPQRSUVWabcdefghjklmnpqrsuvw])(\d{7})([0-9A-Ja-j])$/) : false;

        if (_.isArray(match) && match.length >= 3) {
            number = match[2];
            control = _.isNaN(_.parseInt(match[3])) ? match[3].toUpperCase() : _.parseInt(match[3]);
            for (var i = 0; i < number.length; i++) {
                n = _.parseInt(number[i]);
                n = i % 2 === 0 ? n * 2 : n;
                sum += (n < 10 ? n : n - 9);
            }
            controlDigit = (10 - sum.toString().substr(-1)) % 10;
            controlLetter = 'JABCDEFGHI'.substr(controlDigit, 1);
        }
        return !_.isUndefined(control) && (control === controlDigit || control === controlLetter);
    };

    /**
     * Validate nif format
     * @param {String} nif
     * @return {boolean}
     */
    Utils.isValidNif = function(nif) {
        var lockup = 'TRWAGMYFPDXBNJZSQVHLCKE';
        var nifNumber;
        var nifLetter;
        var isValid = false;
        if (_.isString(nif)) {
            nifNumber = nif.substr(0, nif.length - 1);
            nifLetter = nif.substr(nif.length - 1, 1).toUpperCase();
            isValid = lockup.charAt(nifNumber % 23) === nifLetter;
        }

        return isValid;
    };

    /**
     * Validate Booking Reference format
     * @param {String} pass
     * @return {boolean}
     */
    Utils.isValidBookingRef = function(pass) {
        return Utils.isValidRegex(pass, /^[0-9]{2,18}$/);
    };

    /**
     * Validate a function with name isValid + type
     * @param {String} type
     * @return {boolean}
     */
    Utils.isValid = function(type) {
        return (type) ? Utils['isValid' + type.replace(/^[a-z]/, function(m) {
            return m.toUpperCase();
        })].apply(this, Array.prototype.slice.call(arguments, 1)) : false;
    };

    /**
     * Check that a date is within a range
     * @param {Array} range - two dates from start to end
     * @param {Date} date - interim date
     * @return {boolean}
     */
    Utils.isDateInRange = function(range, date) {
        var format = _.partial(Utils.formatDate, _, Utils.formatDate.masks.isoDate);
        var start;
        var end;
        var next;
        var isInRange = false;

        range = _.compact(range);

        if (_.size(range) === 2 && !!date) {
            start = _.isDate(range[0]) ? range[0] : format(range[0]);
            end = _.isDate(range[1]) ? range[1] : format(range[1]);
            date = _.isDate(date) ? date : format(date);
            next = new Date(end.getTime());
            next.setDate(next.getDate() + 1);
            isInRange = date >= start && date < next;
        }

        return isInRange;
    };

    /**
     * Check that two dates are the same
     * @param {Date} date1 - interim date
     * @param {Date} date2 - interim date
     * @return {boolean}
     */
    Utils.equalDates = function(date1, date2) {
        return (_.isDate(date1) && _.isDate(date2) &&
        date1.getUTCMonth() === date2.getUTCMonth() &&
        date1.getUTCDate() === date2.getUTCDate() &&
        date1.getUTCFullYear() === date2.getUTCFullYear());
    };

    /**
     * Compares if firstDate is a future date. If secondDate is empty compares to now
     * @param {Date} futureDate - Date or milliseconds string.
     * @param {Date} baseDate - Date or milliseconds string. Can be empty
     * @return {boolean} True when firstDate is lower than secondDate
     */
    Utils.isFutureDate = function(futureDate, baseDate) {
        var futureDateMilliseconds = _.isDate(futureDate) ? futureDate : parseInt(futureDate, 10);
        var baseDateMilliseconds = baseDate ? (_.isDate(baseDate) ? baseDate.getTime(): parseInt(baseDate, 10)) : new Date().getTime();
        return futureDateMilliseconds > baseDateMilliseconds;
    };

    /**
     * Default plural rules to follow.
     * @type {[]}
     */
    var defaultPluralRules = [/^\s*(1)\s*$/, /^\s*(\d+)\s*$/];

    /**
     * Pluralizes a text
     * @param  {string} key - should have the form
     *    42|Plural form 0|...|Plural form #
     * @return {string}
     */
    Utils.pluralize = function(key) {
        if (!key) {
            return key;
        }
        Odigeo.SessionScope = Odigeo.SessionScope || {};
        var pluralRules = [].concat(Odigeo.SessionScope.pluralRules || defaultPluralRules);
        var pluralForms;
        var arg;

        if (/^_pl0/.test(key)) {
            pluralRules.unshift(/^\s*(0)\s*$/);
        }
        // removes _pl0?( ) from the key
        key = key.replace(/_pl0?\(([^\]]*)\)/, '$1');

        pluralForms = key.split('|');
        arg = pluralForms.shift();

        for (var i = 0; i < pluralRules.length; i++) {
            if (RegExp(pluralRules[i]).test(arg)) {
                return (pluralForms[i] || '').replace('#', arg);
            }
        }
        // non valid argument: empty or not a number
        return key;
    };

    /**
     * Pluralizes a CMS key. It can contain any number of them
     * @param  {string} key - could contain plurals
     * @param  {...number} arg - as many params as required on the key
     * @return {string}
     */
    Utils.pluralizeAll = function(key) {
        if (!_.isString(key)) {
            return key;
        }
        var args = _.toArray(arguments);
        args.shift();
        var plRegEx = /\$\[(_pl0?\([^\]]*\))\]/;
        var argRegEx = /%(\d+)\$s/;
        var pluralize = Utils.pluralize;
        var plMatch;
        var argMatch;

        // replaces all the %x$s args with its values
        while ((argMatch = key.match(argRegEx)) !== null) {
            key = key.replace(argRegEx, args[argMatch[1] - 1]);
        }
        // pluralizes every possible plural
        while ((plMatch = key.match(plRegEx)) !== null) {
            key = key.replace(plRegEx, pluralize(plMatch[1]));
        }
        return key;
    };

    /**
     * Replace %1$s, %2$s, ..., %n$s values in cms keys
     * @param  {string} key - cms key with arguments
     * @param  {...number} arg - as many params as required on the key
     * @return {string}
     */
    Utils.replaceCmsKeyArgs = function(key) {
        if (_.isString(key)) {
            var args = _.toArray(arguments);
            args.shift();
            for (var i = 1; i <= args.length; i++) {
                key = key.replace(new RegExp('%' + i + '\\$s', 'g'), args[i-1]);
            }
        }
        return key;
    };

    /**
     * Insert substring in a given position of string
     * @param  {string} text - complete string
     * @param  {string} middle - substring to insert
     * @param  {int} position - position of the insert
     * @return {string}
     */
    Utils.insertMiddleString = function(text, middle, position) {
        if (position !== 0) {
            return text.substring(0, position) + middle + text.substring(position, text.length);
        } else {
            return middle + text;
        }
    };

    /**
     * Wrap #HLE# and #HLS# in regex substring of a string
     * @param  {string} text - complete string
     * @param  {RegExp} regExp - substring to search and replace
     * @return {string}
     */
    Utils.replacer = function(text, regExp) {
        var filteredText = Utils.replaceNonAlphaNumeric(text, ' ', true);
        var match = regExp.exec(filteredText);
        var replacerPos = [];

        while (match) {
            replacerPos.push([match.index, regExp.lastIndex]);
            match = regExp.exec(filteredText);
        }
        _.each(_.sortBy(replacerPos, function(pair) {
            return -1 * pair[0];
        }), function(pair) {
            text = Utils.insertMiddleString(text, '#HLE#', pair[1]);
            text = Utils.insertMiddleString(text, '#HLS#', pair[0]);
        });

        return text;
    };

    /**
     * Transform text into an array representing multiple-line paragraphs
     *
     * @param {String} text - paragraph to parse with break lines
     * @return {String[]}
     */
    Utils.breakTextToMultipleLines = function(text) {
        var BREAK_LINES = /#br#/g;
        var cleanerText = [
            {regex: /\n|(\.|:)\n/g, replace: '$1#br#'},
            {regex: /\s+(\.)\s*/g, replace: '$1 '},
            {regex: /\s*(:)\s*/g, replace: '$1 '},
        ];

        _.map(cleanerText, function(cleaner) {
            text = text.replace(cleaner.regex, cleaner.replace);
        });

        return Odigeo.Utils.isValidRegex(text, BREAK_LINES) ? _.compact(text.split(BREAK_LINES)) : [text];
    };

    /**
     * Setting object for processCmsPlaceholder method (private)
     * @type {Object}
     */
    var CmsPlaceholderSettings = {
        placeholder: 'value',
        side: 'front',
    };

    /**
     * Check if cms text has a placeholder (private)
     *
     * @param  {String} text
     * @param  {String} placeholder
     *
     * @return {Boolean}
     */
    var hasCmsPlaceholder = function(text, placeholder) {
        var regex = new RegExp('%= ?' + placeholder + ' ?%', 'i');
        return text && text.match(regex);
    };

    /**
     * Replace a placeholder in cms text with underscore template (private)
     *
     * @param  {String} text
     * @param  {String} placeholder
     * @param  {Number|String} val
     *
     * @return {String}
     */
    var replaceCmsPlaceholder = function(text, placeholder, val) {
        var tpl = _.template(text, null, {interpolate: /%=(.+?)%/g});
        var data = {};
        data[placeholder] = val;
        return tpl(data);
    };

    /**
     * Process a text with an associated value
     *
     * @param  {Number|String} val
     * @param  {String} text
     * @param  {Object} settings
     *
     * @return {String}
     */
    Utils.processCmsPlaceholder = function(val, text, settings) {
        settings = _.defaults({}, settings, CmsPlaceholderSettings);

        if (hasCmsPlaceholder(text, settings.placeholder)) {
            text = replaceCmsPlaceholder(text, settings.placeholder, val);
        } else {
            text = settings.side === 'front' ? val + ' ' + text :
                text + ' ' + val;
        }

        return text;
    };

    Utils.getCurrentPath = function() {
        return document.location.pathname;
    };

    Utils.isWebmasterPage = function() {
        return $('#page').data('wmp') === true;
    };

    /**
     * Returns the number of remaining days to a given date.
     * Note that if the input belongs to the past the result will be negative.
     * @param {number} timestamp
     * @return {number} of days
     */
    Utils.getRemainingDays = function(timestamp) {
        return Utils.getDifferenceInDays(new Date().getTime(), timestamp);
    };

    /**
     * Returns the number of remaining minutes to a given date.
     * Note that if the input belongs to the past the result will be negative.
     * @param {number} timestamp
     * @return {number} of minutes
     */
    Utils.getRemainingMinutes = function(timestamp) {
        return Utils.getDifferenceInMinutes(new Date().getTime(), timestamp);
    };

    /**
     * Returns the number of days between two timestamps.
     * @param {number} start timestamp
     * @param {number} end timestamp
     * @return {number} of days
     */
    Utils.getDifferenceInDays = function(start, end) {
        var daysDividing = 24 * 60 * 60 * 1000;
        return getTimeDifference(start, end, daysDividing);
    };

    /**
     * Returns the number of minutes between two timestamps.
     * @param {number} start timestamp
     * @param {number} end timestamp
     * @return {number} of minutes
     */
    Utils.getDifferenceInMinutes = function(start, end) {
        var minutesDividing = 60 * 1000;
        return getTimeDifference(start, end, minutesDividing);
    };

    /**
     * Returns the number of seconds between two timestamps.
     * @param {number} start timestamp
     * @param {number} end timestamp
     * @return {number} of seconds
     */
    Utils.getDifferenceInSeconds = function(start, end) {
        var secondsDividing = 1000;
        return getTimeDifference(start, end, secondsDividing);
    };

    /**
     * Returns the number of milliseconds between two timestamps.
     * @param {number} start timestamp
     * @param {number} end timestamp
     * @return {number} of milliseconds
     */
    Utils.getDifferenceInMilliseconds = function(start, end) {
        var millisecondsDividing = 1;
        return getTimeDifference(start, end, millisecondsDividing);
    };

    var getTimeDifference = function(start, end, timeUnitDividing) {
        return Math.floor(end / timeUnitDividing) - Math.floor(start / timeUnitDividing);
    };

    var hashCode = function(string) {
        var hash = 0;
        if (Array.prototype.reduce) {
            return string.split('').reduce(function(a, b) {
                a = ((a << 5) - a) + b.charCodeAt(0);
                return a & a;
            }, 0);
        }
        for (var i = 0; i < string.length; i++) {
            var character = string.charCodeAt(i);
            hash = ((hash << 5) - hash) + character;
            hash = hash & hash; // Convert to 32bit integer
        }
        return hash;
    };

    /**
     * Returns the path of the image from CDN
     * @param {String} resourcePath path
     * @return {String} path
     */
    Utils.getImageFromCDN = function(resourcePath) {
        var links = document.getElementsByTagName('link') || [];
        var domains = [];
        for (var i = 0; i < links.length; i++) {
            if (links[i].getAttribute('rel') === 'dns-prefetch') {
                domains.push(links[i].getAttribute('href') + '/images/');
            }
        }
        if (domains.length > 0) {
            var index = Math.abs(hashCode(resourcePath)) % domains.length;
            return domains[index] + resourcePath;
        } else {
            return '/images/' + resourcePath;
        }
    };

    /**
     * Replaces All non alphanumeric (in this case, accents are not considered) withthe passed snippet or
     * an empty string if no replacement specified. Additionally, if replaceAccents is set to true, all accents
     * will be with Utils.replaceAccents function.
     * @param {string} string The String to be replaced
     * @param {string} [replacement=''] The replacement string
     * @param {boolean} replaceAccents if true, Utils.replaceAccents will be called
     * @returns {string}
     */

    Utils.replaceNonAlphaNumeric = function(string, replacement, replaceAccents) {
        var text = replaceAccents ? Odigeo.Utils.replaceAccents(string) : string;
        return text.replace(/[-'/\\´`.,\s]/gi, replacement ? replacement : '');
    };

    Utils.replaceAccents = (function() {
        var diacriticsMapUpper = {
            'A': new RegExp('[\\u0041\\u24B6\\uFF21\\u00C0\\u00C1\\u00C2\\u1EA6\\u1EA4\\u1EAA\\u1EA8\\u00C3\\u0100\\u0102\\u1EB0\\u1EAE\\u1EB4' +
                '\\u1EB2\\u0226\\u01E0\\u00C4\\u01DE\\u1EA2\\u00C5\\u01FA\\u01CD\\u0200\\u0202\\u1EA0\\u1EAC\\u1EB6\\u1E00\\u0104' +
                '\\u023A\\u2C6F\\uA732\\u00C6\\u01FC\\u01E2\\uA734\\uA736\\uA738\\uA73A\\uA73C]', 'g'),
            'B': new RegExp('[\\u0042\\u24B7\\uFF22\\u1E02\\u1E04\\u1E06\\u0243\\u0182\\u0181]', 'g'),
            'C': new RegExp('[\\u0043\\u24B8\\uFF23\\u0106\\u0108\\u010A\\u010C\\u00C7\\u1E08\\u0187\\u023B\\uA73E]', 'g'),
            'D': new RegExp('[\\u0044\\u24B9\\uFF24\\u1E0A\\u010E\\u1E0C\\u1E10\\u1E12\\u1E0E\\u0110\\u018B\\u018A\\u0189\\uA779\\u01F1\\u01C4' +
                '\\u01F2\\u01C5]', 'g'),
            'E': new RegExp('[\\u0045\\u24BA\\uFF25\\u00C8\\u00C9\\u00CA\\u1EC0\\u1EBE\\u1EC4\\u1EC2\\u1EBC\\u0112\\u1E14\\u1E16\\u0114\\u0116' +
                '\\u00CB\\u1EBA\\u011A\\u0204\\u0206\\u1EB8\\u1EC6\\u0228\\u1E1C\\u0118\\u1E18\\u1E1A\\u0190\\u018E]', 'g'),
            'F': new RegExp('[\\u0046\\u24BB\\uFF26\\u1E1E\\u0191\\uA77B]', 'g'),
            'G': new RegExp('[\\u0047\\u24BC\\uFF27\\u01F4\\u011C\\u1E20\\u011E\\u0120\\u01E6\\u0122\\u01E4\\u0193\\uA7A0\\uA77D\\uA77E]', 'g'),
            'H': new RegExp('[\\u0048\\u24BD\\uFF28\\u0124\\u1E22\\u1E26\\u021E\\u1E24\\u1E28\\u1E2A\\u0126\\u2C67\\u2C75\\uA78D]', 'g'),
            'I': new RegExp('[\\u0049\\u24BE\\uFF29\\u00CC\\u00CD\\u00CE\\u0128\\u012A\\u012C\\u0130\\u00CF\\u1E2E\\u1EC8\\u01CF\\u0208\\u020A' +
                '\\u1ECA\\u012E\\u1E2C\\u0197]', 'g'),
            'J': new RegExp('[\\u004A\\u24BF\\uFF2A\\u0134\\u0248]', 'g'),
            'K': new RegExp('[\\u004B\\u24C0\\uFF2B\\u1E30\\u01E8\\u1E32\\u0136\\u1E34\\u0198\\u2C69\\uA740\\uA742\\uA744\\uA7A2]', 'g'),
            'L': new RegExp('[\\u004C\\u24C1\\uFF2C\\u013F\\u0139\\u013D\\u1E36\\u1E38\\u013B\\u1E3C\\u1E3A\\u0141\\u023D\\u2C62\\u2C60\\uA748' +
                '\\uA746\\uA780\\u01C7\\u01C8]', 'g'),
            'M': new RegExp('[\\u004D\\u24C2\\uFF2D\\u1E3E\\u1E40\\u1E42\\u2C6E\\u019C]', 'g'),
            'N': new RegExp('[\\u004E\\u24C3\\uFF2E\\u01F8\\u0143\\u00D1\\u1E44\\u0147\\u1E46\\u0145\\u1E4A\\u1E48\\u0220\\u019D\\uA790\\uA7A4' +
                '\\u01CA\\u01CB]', 'g'),
            'O': new RegExp('[\\u004F\\u24C4\\uFF2F\\u00D2\\u00D3\\u00D4\\u1ED2\\u1ED0\\u1ED6\\u1ED4\\u00D5\\u1E4C\\u022C\\u1E4E\\u014C\\u1E50' +
                '\\u1E52\\u014E\\u022E\\u0230\\u00D6\\u022A\\u1ECE\\u0150\\u01D1\\u020C\\u020E\\u01A0\\u1EDC\\u1EDA\\u1EE0\\u1EDE' +
                '\\u1EE2\\u1ECC\\u1ED8\\u01EA\\u01EC\\u00D8\\u01FE\\u0186\\u019F\\uA74A\\uA74C\\u01A2\\uA74E\\u0222]', 'g'),
            'P': new RegExp('[\\u0050\\u24C5\\uFF30\\u1E54\\u1E56\\u01A4\\u2C63\\uA750\\uA752\\uA754]', 'g'),
            'Q': new RegExp('[\\u0051\\u24C6\\uFF31\\uA756\\uA758\\u024A]', 'g'),
            'R': new RegExp('[\\u0052\\u24C7\\uFF32\\u0154\\u1E58\\u0158\\u0210\\u0212\\u1E5A\\u1E5C\\u0156\\u1E5E\\u024C\\u2C64\\uA75A\\uA7A6' +
                '\\uA782]', 'g'),
            'S': new RegExp('[\\u0053\\u24C8\\uFF33\\u1E9E\\u015A\\u1E64\\u015C\\u1E60\\u0160\\u1E66\\u1E62\\u1E68\\u0218\\u015E\\u2C7E\\uA7A8' +
                '\\uA784]', 'g'),
            'T': new RegExp('[\\u0054\\u24C9\\uFF34\\u1E6A\\u0164\\u1E6C\\u021A\\u0162\\u1E70\\u1E6E\\u0166\\u01AC\\u01AE\\u023E\\uA786' +
                '\\uA728]', 'g'),
            'U': new RegExp('[\\u0055\\u24CA\\uFF35\\u00D9\\u00DA\\u00DB\\u0168\\u1E78\\u016A\\u1E7A\\u016C\\u00DC\\u01DB\\u01D7\\u01D5\\u01D9' +
                '\\u1EE6\\u016E\\u0170\\u01D3\\u0214\\u0216\\u01AF\\u1EEA\\u1EE8\\u1EEE\\u1EEC\\u1EF0\\u1EE4\\u1E72\\u0172\\u1E76' +
                '\\u1E74\\u0244]', 'g'),
            'V': new RegExp('[\\u0056\\u24CB\\uFF36\\u1E7C\\u1E7E\\u01B2\\uA75E\\u0245\\uA760]', 'g'),
            'W': new RegExp('[\\u0057\\u24CC\\uFF37\\u1E80\\u1E82\\u0174\\u1E86\\u1E84\\u1E88\\u2C72]', 'g'),
            'X': new RegExp('[\\u0058\\u24CD\\uFF38\\u1E8A\\u1E8C]', 'g'),
            'Y': new RegExp('[\\u0059\\u24CE\\uFF39\\u1EF2\\u00DD\\u0176\\u1EF8\\u0232\\u1E8E\\u0178\\u1EF6\\u1EF4\\u01B3\\u024E\\u1EFE]', 'g'),
            'Z': new RegExp('[\\u005A\\u24CF\\uFF3A\\u0179\\u1E90\\u017B\\u017D\\u1E92\\u1E94\\u01B5\\u0224\\u2C7F\\u2C6B\\uA762]', 'g'),
        };
        var diacriticsMapLower = {
            'a': new RegExp('[\\u0061\\u24D0\\uFF41\\u1E9A\\u00E0\\u00E1\\u00E2\\u1EA7\\u1EA5\\u1EAB\\u1EA9\\u00E3\\u0101\\u0103\\u1EB1\\u1EAF' +
                '\\u1EB5\\u1EB3\\u0227\\u01E1\\u00E4\\u01DF\\u1EA3\\u00E5\\u01FB\\u01CE\\u0201\\u0203\\u1EA1\\u1EAD\\u1EB7\\u1E01' +
                '\\u0105\\u2C65\\u0250\\uA733\\u00E6\\u01FD\\u01E3\\uA735\\uA737\\uA739\\uA73B\\uA73D]', 'g'),
            'b': new RegExp('[\\u0062\\u24D1\\uFF42\\u1E03\\u1E05\\u1E07\\u0180\\u0183\\u0253]', 'g'),
            'c': new RegExp('[\\u0063\\u24D2\\uFF43\\u0107\\u0109\\u010B\\u010D\\u00E7\\u1E09\\u0188\\u023C\\uA73F\\u2184]', 'g'),
            'd': new RegExp('[\\u0064\\u24D3\\uFF44\\u1E0B\\u010F\\u1E0D\\u1E11\\u1E13\\u1E0F\\u0111\\u018C\\u0256\\u0257\\uA77A\\u01F3' +
                '\\u01C6]', 'g'),
            'e': new RegExp('[\\u0065\\u24D4\\uFF45\\u00E8\\u00E9\\u00EA\\u1EC1\\u1EBF\\u1EC5\\u1EC3\\u1EBD\\u0113\\u1E15\\u1E17\\u0115\\u0117' +
                '\\u00EB\\u1EBB\\u011B\\u0205\\u0207\\u1EB9\\u1EC7\\u0229\\u1E1D\\u0119\\u1E19\\u1E1B\\u0247\\u025B\\u01DD]', 'g'),
            'f': new RegExp('[\\u0066\\u24D5\\uFF46\\u1E1F\\u0192\\uA77C]', 'g'),
            'g': new RegExp('[\\u0067\\u24D6\\uFF47\\u01F5\\u011D\\u1E21\\u011F\\u0121\\u01E7\\u0123\\u01E5\\u0260\\uA7A1\\u1D79\\uA77F]', 'g'),
            'h': new RegExp('[\\u0068\\u24D7\\uFF48\\u0125\\u1E23\\u1E27\\u021F\\u1E25\\u1E29\\u1E2B\\u1E96\\u0127\\u2C68\\u2C76\\u0265' +
                '\\u0195]', 'g'),
            'i': new RegExp('[\\u0069\\u24D8\\uFF49\\u00EC\\u00ED\\u00EE\\u0129\\u012B\\u012D\\u00EF\\u1E2F\\u1EC9\\u01D0\\u0209\\u020B\\u1ECB' +
                '\\u012F\\u1E2D\\u0268\\u0131]', 'g'),
            'j': new RegExp('[\\u006A\\u24D9\\uFF4A\\u0135\\u01F0\\u0249]', 'g'),
            'k': new RegExp('[\\u006B\\u24DA\\uFF4B\\u1E31\\u01E9\\u1E33\\u0137\\u1E35\\u0199\\u2C6A\\uA741\\uA743\\uA745\\uA7A3]', 'g'),
            'l': new RegExp('[\\u006C\\u24DB\\uFF4C\\u0140\\u013A\\u013E\\u1E37\\u1E39\\u013C\\u1E3D\\u1E3B\\u017F\\u0142\\u019A\\u026B\\u2C61' +
                '\\uA749\\uA781\\uA747\\u01C9]', 'g'),
            'm': new RegExp('[\\u006D\\u24DC\\uFF4D\\u1E3F\\u1E41\\u1E43\\u0271\\u026F]', 'g'),
            'n': new RegExp('[\\u006E\\u24DD\\uFF4E\\u01F9\\u0144\\u00F1\\u1E45\\u0148\\u1E47\\u0146\\u1E4B\\u1E49\\u019E\\u0272\\u0149\\uA791' +
                '\\uA7A5\\u01CC]', 'g'),
            'o': new RegExp('[\\u006F\\u24DE\\uFF4F\\u00F2\\u00F3\\u00F4\\u1ED3\\u1ED1\\u1ED7\\u1ED5\\u00F5\\u1E4D\\u022D\\u1E4F\\u014D\\u1E51' +
                '\\u1E53\\u014F\\u022F\\u0231\\u00F6\\u022B\\u1ECF\\u0151\\u01D2\\u020D\\u020F\\u01A1\\u1EDD\\u1EDB\\u1EE1\\u1EDF' +
                '\\u1EE3\\u1ECD\\u1ED9\\u01EB\\u01ED\\u00F8\\u01FF\\u0254\\uA74B\\uA74D\\u0275\\u01A3\\u0223\\uA74F]', 'g'),
            'p': new RegExp('[\\u0070\\u24DF\\uFF50\\u1E55\\u1E57\\u01A5\\u1D7D\\uA751\\uA753\\uA755]', 'g'),
            'q': new RegExp('[\\u0071\\u24E0\\uFF51\\u024B\\uA757\\uA759]', 'g'),
            'r': new RegExp('[\\u0072\\u24E1\\uFF52\\u0155\\u1E59\\u0159\\u0211\\u0213\\u1E5B\\u1E5D\\u0157\\u1E5F\\u024D\\u027D\\uA75B\\uA7A7' +
                '\\uA783]', 'g'),
            's': new RegExp('[\\u0073\\u24E2\\uFF53\\u00DF\\u015B\\u1E65\\u015D\\u1E61\\u0161\\u1E67\\u1E63\\u1E69\\u0219\\u015F\\u023F\\uA7A9' +
                '\\uA785\\u1E9B]', 'g'),
            't': new RegExp('[\\u0074\\u24E3\\uFF54\\u1E6B\\u1E97\\u0165\\u1E6D\\u021B\\u0163\\u1E71\\u1E6F\\u0167\\u01AD\\u0288\\u2C66\\uA787' +
                '\\uA729]', 'g'),
            'u': new RegExp('[\\u0075\\u24E4\\uFF55\\u00F9\\u00FA\\u00FB\\u0169\\u1E79\\u016B\\u1E7B\\u016D\\u00FC\\u01DC\\u01D8\\u01D6\\u01DA' +
                '\\u1EE7\\u016F\\u0171\\u01D4\\u0215\\u0217\\u01B0\\u1EEB\\u1EE9\\u1EEF\\u1EED\\u1EF1\\u1EE5\\u1E73\\u0173\\u1E77' +
                '\\u1E75\\u0289]', 'g'),
            'v': new RegExp('[\\u0076\\u24E5\\uFF56\\u1E7D\\u1E7F\\u028B\\uA75F\\u028C\\uA761]', 'g'),
            'w': new RegExp('[\\u0077\\u24E6\\uFF57\\u1E81\\u1E83\\u0175\\u1E87\\u1E85\\u1E98\\u1E89\\u2C73]', 'g'),
            'x': new RegExp('[\\u0078\\u24E7\\uFF58\\u1E8B\\u1E8D]', 'g'),
            'y': new RegExp('[\\u0079\\u24E8\\uFF59\\u1EF3\\u00FD\\u0177\\u1EF9\\u0233\\u1E8F\\u00FF\\u1EF7\\u1E99\\u1EF5\\u01B4\\u024F' +
                '\\u1EFF]', 'g'),
            'z': new RegExp('[\\u007A\\u24E9\\uFF5A\\u017A\\u1E91\\u017C\\u017E\\u1E93\\u1E95\\u01B6\\u0225\\u0240\\u2C6C\\uA763]', 'g'),
        };

        /**
         * Replaces especial characters with their ASCII equivalents
         * @param {string} stringToReplace
         * @param {boolean} lowerOnly - [lowerOnly=false] if only lower case letters should be replaced.
         * For instance if the give strong is in lower case.
         * @return {string} in ASCII
         */
        return function(stringToReplace, lowerOnly) {
            _.each(diacriticsMapLower, function(specialCharsList, baseCharacter) {
                stringToReplace = stringToReplace.replace(specialCharsList, baseCharacter);
            });

            if (!lowerOnly) {
                _.each(diacriticsMapUpper, function(specialCharsList, baseCharacter) {
                    stringToReplace = stringToReplace.replace(specialCharsList, baseCharacter);
                });
            }
            return stringToReplace;
        };
    }());

    // Time for expiration (this is/should be overridden by headers.jsp)
    Utils.sessionLifeTime = 1200000;

    /** @namespace */

    /** Expiration Counter Object.
     *
     * A global object to be used to control session expiration times. Expiration time is set with
     * setTime function <b>in seconds</b>. Once set the timer starts, stop() and reset() can be called.
     * An isRunning function can be called to know if the timer is indeed running.
     * When the time lapse passes, an event "session:expired" is triggered.
     * Also, another event is triggered when a lapse of {expirationTime - expirationTime/4} passes
     * (second argument of setTime will override this value if present) this is mainly to add an
     * alert informing that the session is about to expire.
     *
     * <b>NOTE: </b>setTime function doesn't reset the timer, nor stops it.
     * @type {object}
     * @static
     *
     */
    Utils.sessionTimer = (function() {
        var CONST = {
            SESSION_EXPIRED_EVENT: 'session:expired',
            SESSION_ABOUT_TO_EXPIRE_EVENT: 'session:abouttoexpire',
            SESSION_LOGIN_EXPIRATION_EVENT: 'session:loginexpiration',
        };

        var _timer = null;
        var _alertTimer = null;
        var _loginTimer = null;
        var _time = Utils.sessionLifeTime;
        var _alertTime = _time - (_time / 4);
        var _loginTime = 1200000;
        var _isRunning = false;

        var timeoutHandler = function() {
            stop();
            Odigeo.trigger(CONST.SESSION_EXPIRED_EVENT);
        };

        var alertTimeoutHandler = function() {
            Odigeo.trigger(CONST.SESSION_ABOUT_TO_EXPIRE_EVENT);
        };

        var loginExpirationHandler = function() {
            Odigeo.trigger(CONST.SESSION_LOGIN_EXPIRATION_EVENT);
        };

        var start = function() {
            if (_time > 0 && !_isRunning) {
                _isRunning = true;
                _timer = setTimeout(timeoutHandler, _time);
                _alertTimer = setTimeout(alertTimeoutHandler, _alertTime);
                _loginTimer = setTimeout(loginExpirationHandler, _loginTime);
            }
        };

        var stop = function() {
            if (_isRunning) {
                clearTimeout(_timer);
                clearTimeout(_alertTimer);
                clearTimeout(_loginTimer);
                _isRunning = false;
            }
        };

        var clearAlertTimer = function() {
            clearTimeout(_alertTimer);
        };

        var reset = function() {
            stop();
            start();
        };

        var setTime = function(time, alertTime) {
            _time = time * 1000;
            _alertTime = alertTime && time > alertTime ? alertTime * 1000 : _time - (_time / 4);
            reset();
        };

        var isRunning = function() {
            return _isRunning;
        };

        return {
            CONST: CONST,
            stop: stop,
            start: start,
            clearAlertTimer: clearAlertTimer,
            reset: reset,
            setTime: setTime,
            isRunning: isRunning,
        };
    }());

    /**
     * Define a currency model.
     */
    Utils.Currency = {
        currencySign: '&euro;',
        currencyPosition: 'back', // front, back, none
        decimalPlaces: 2,
        decimalSeparator: ',',
        thousandSeparator: '.',
        currencySymbolPrefix: '',
        spaceBetweenSignAndQuantity: true,
        signBetweenSymbolAndValue: false,
        negationSymbol: '-',
    };

    /**
     * Evaluates if a given string starts with a substring.
     * @param {String} string - Given string.
     * @param {String} searchString - Search starting string.
     * @return {boolean} True if string starts with searchString, false in any other case.
     */
    Utils.startsWith = function(string, searchString) {
        return !_.isEmpty(string) && searchString && string.substr(0, searchString.length) === searchString;
    };

    /**
     * Format the currency using a currency model.
     * @param {String|Number} qty - Quantity to be formatted.
     * @param {Object} [currency = Utils.Currency] - Currency model to be used. Param is optional
     * @return {String} The quantity properly formatted according to the Currency model.
     */
    Utils.formatCurrency = function(qty, currency) {
        if (_.isEmpty(currency)) {
            currency = Utils.Currency;
        }
        var str;
        var ths;
        var neg = qty < 0 ? currency.negationSymbol : '';
        var dec = parseInt(qty = Math.abs(+qty || 0).toFixed(currency.decimalPlaces), 10) + '';
        ths = (ths = dec.length) > 3 ? ths % 3 : 0;
        str = ths ? dec.substr(0, ths) + currency.thousandSeparator : '';
        str += dec.substr(ths).replace(/(\d{3})(?=\d)/g, '$1' + currency.thousandSeparator);
        str += (currency.decimalPlaces > 0 ? currency.decimalSeparator + Math.abs(qty - dec).toFixed(currency.decimalPlaces).slice(2) : '');
        if (currency.currencyPosition === 'back') {
            str = neg + str + (currency.spaceBetweenSignAndQuantity ? ' ' : '') + currency.currencySymbolPrefix + currency.currencySign;
        } else if (currency.currencyPosition === 'front') {
            str = (currency.signBetweenSymbolAndValue ? '' : neg) + currency.currencySymbolPrefix + currency.currencySign +
                (currency.spaceBetweenSignAndQuantity ? ' ' : '') + (currency.signBetweenSymbolAndValue ? neg : '') + str;
        }
        return str;
    };

    /**
     * Format a split object currency using a currency model.
     * @param {String|Number} qty - Quantity to be formatted.
     * @param {Object} [currency = Utils.Currency] - Currency model to be used. Param is optional
     * @return {Object} The split currency properly formatted according to the Currency model.
     */
    Utils.splitFormatedCurrency = function(qty, currency) {
        var price = {
            prefix: null,
            integer: 0,
            decimal: 0,
            sufix: null,
            quantity: qty,
        };
        var neg = qty < 0 ? '-' : '';
        var dec;
        var espace;
        var ths;
        var integerPrice;

        currency = currency || Utils.Currency;
        dec = parseInt(qty = Math.abs(+qty || 0).toFixed(currency.decimalPlaces), 10) + '';
        espace = (currency.spaceBetweenSignAndQuantity ? ' ' : '');
        ths = (ths = dec.length) > 3 ? ths % 3 : 0;
        integerPrice = ths ? dec.substr(0, ths) + currency.thousandSeparator : '';
        price.integer = (currency.signBetweenSymbolAndValue ? '' : neg) +
            (integerPrice + dec.substr(ths).replace(/(\d{3})(?=\d)/g, '$1' + currency.thousandSeparator));
        price.decimal = (currency.decimalPlaces > 0 ? currency.decimalSeparator + Math.abs(qty - dec).toFixed(currency.decimalPlaces).slice(2) : '');
        if (currency.currencyPosition === 'back') {
            price.sufix = espace + currency.currencySymbolPrefix + currency.currencySign;
        } else if (currency.currencyPosition === 'front') {
            price.prefix = currency.currencySymbolPrefix + currency.currencySign + espace;
        }

        return price;
    };

    Utils.encodeToHtmlEntity = function(strToEncode) {
        var encodedText;
        if (_.isString(strToEncode)) {
            encodedText = _.startsWith(strToEncode.trim(), '&') ? strToEncode : strToEncode.replace(/[\u00A0-\u9999<>&]/gim, function(i) {
                return '&#'+i.charCodeAt(0)+';';
            });
        }
        return encodedText;
    };

    Utils.Easing = (function() {
        /**
         * Based on easing equations by Robert Penner's
         * http://www.gizma.com/easing/
         */

        var easingFunctions = {};

        // simple linear tweening - no easing, no acceleration
        easingFunctions.linearTween = function(t, b, c, d) {
            return c * t / d + b;
        };

        // quadratic easing in - accelerating from zero velocity
        easingFunctions.easeInQuad = function(t, b, c, d) {
            t /= d;
            return c * t * t + b;
        };

        // quadratic easing out - decelerating to zero velocity
        easingFunctions.easeOutQuad = function(t, b, c, d) {
            t /= d;
            return -c * t * (t - 2) + b;
        };

        // quadratic easing in/out - acceleration until halfway, then deceleration
        easingFunctions.easeInOutQuad = function(t, b, c, d) {
            t /= d / 2;
            if (t < 1) {
                return c / 2 * t * t + b;
            }
            t--;
            return -c / 2 * (t * (t - 2) - 1) + b;
        };

        // cubic easing in - accelerating from zero velocity
        easingFunctions.easeInCubic = function(t, b, c, d) {
            t /= d;
            return c * t * t * t + b;
        };

        // cubic easing out - decelerating to zero velocity
        easingFunctions.easeOutCubic = function(t, b, c, d) {
            t /= d;
            t--;
            return c * (t * t * t + 1) + b;
        };

        // cubic easing in/out - acceleration until halfway, then deceleration
        easingFunctions.easeInOutCubic = function(t, b, c, d) {
            t /= d / 2;
            if (t < 1) {
                return c / 2 * t * t * t + b;
            }
            t -= 2;
            return c / 2 * (t * t * t + 2) + b;
        };

        // quartic easing in - accelerating from zero velocity
        easingFunctions.easeInQuart = function(t, b, c, d) {
            t /= d;
            return c * t * t * t * t + b;
        };

        // quartic easing out - decelerating to zero velocity
        easingFunctions.easeOutQuart = function(t, b, c, d) {
            t /= d;
            t--;
            return -c * (t * t * t * t - 1) + b;
        };

        // quartic easing in/out - acceleration until halfway, then deceleration
        easingFunctions.easeInOutQuart = function(t, b, c, d) {
            t /= d / 2;
            if (t < 1) {
                return c / 2 * t * t * t * t + b;
            }
            t -= 2;
            return -c / 2 * (t * t * t * t - 2) + b;
        };

        // quintic easing in - accelerating from zero velocity
        easingFunctions.easeInQuint = function(t, b, c, d) {
            t /= d;
            return c * t * t * t * t * t + b;
        };

        // quintic easing out - decelerating to zero velocity
        easingFunctions.easeOutQuint = function(t, b, c, d) {
            t /= d;
            t--;
            return c * (t * t * t * t * t + 1) + b;
        };

        // quintic easing in/out - acceleration until halfway, then deceleration
        easingFunctions.easeInOutQuint = function(t, b, c, d) {
            t /= d / 2;
            if (t < 1) {
                return c / 2 * t * t * t * t * t + b;
            }
            t -= 2;
            return c / 2 * (t * t * t * t * t + 2) + b;
        };

        // sinusoidal easing in - accelerating from zero velocity
        easingFunctions.easeInSine = function(t, b, c, d) {
            return -c * Math.cos(t / d * (Math.PI / 2)) + c + b;
        };

        // sinusoidal easing out - decelerating to zero velocity
        easingFunctions.easeOutSine = function(t, b, c, d) {
            return c * Math.sin(t / d * (Math.PI / 2)) + b;
        };

        // sinusoidal easing in/out - accelerating until halfway, then decelerating
        easingFunctions.easeInOutSine = function(t, b, c, d) {
            return -c / 2 * (Math.cos(Math.PI * t / d) - 1) + b;
        };

        // exponential easing in - accelerating from zero velocity
        easingFunctions.easeInExpo = function(t, b, c, d) {
            return c * Math.pow(2, 10 * (t / d - 1)) + b;
        };

        // exponential easing out - decelerating to zero velocity
        easingFunctions.easeOutExpo = function(t, b, c, d) {
            return c * (-Math.pow(2, -10 * t / d) + 1) + b;
        };

        // exponential easing in/out - accelerating until halfway, then decelerating
        easingFunctions.easeInOutExpo = function(t, b, c, d) {
            t /= d / 2;
            if (t < 1) {
                return c / 2 * Math.pow(2, 10 * (t - 1)) + b;
            }
            t--;
            return c / 2 * (-Math.pow(2, -10 * t) + 2) + b;
        };

        // circular easing in - accelerating from zero velocity
        easingFunctions.easeInCirc = function(t, b, c, d) {
            t /= d;
            return -c * (Math.sqrt(1 - t * t) - 1) + b;
        };

        // circular easing out - decelerating to zero velocity
        easingFunctions.easeOutCirc = function(t, b, c, d) {
            t /= d;
            t--;
            return c * Math.sqrt(1 - t * t) + b;
        };

        // circular easing in/out - acceleration until halfway, then deceleration
        easingFunctions.easeInOutCirc = function(t, b, c, d) {
            t /= d / 2;
            if (t < 1) {
                return -c / 2 * (Math.sqrt(1 - t * t) - 1) + b;
            }
            t -= 2;
            return c / 2 * (Math.sqrt(1 - t * t) + 1) + b;
        };

        easingFunctions.randomEquation = function() {
            var easingArray = _.toArray(easingFunctions).slice(0, -1);
            return easingArray[Math.floor(Math.random() * easingArray.length)];
        };

        return easingFunctions;
    }());

    /**
     * Get the object where a property is located, for example:
     * var obj = {a: {c: {b: 'property}}}, getObjRef(obj, 'a.c.b') return {b: 'property'}
     * @param {Object} obj
     * @param {Array} path - of Strings
     * @return {Object}
     */
    Utils.getObjRef = function(obj, path) {
        var objRef = {};
        if (_.isArray(path) && _.isObject(obj)) {
            objRef = obj;
            for (var i = 0; i <= path.length - 1 && _.isObject(objRef); i++) {
                if (i < (path.length -1)) {
                    objRef = objRef[path[i]];
                } else if (_.isUndefined(objRef[path[i]])) {
                    objRef = {};
                }
            }
        }
        return _.isObject(objRef) ? objRef : {};
    };

    /**
     * Get the value of object properties that can be nested in any level, for example:
     * var obj = {a: {c: {b: 'property}}}, you can get b doing getNestedProperty(obj, 'a.c.b')
     * @param {Object} obj
     * @param {String} prop
     * @return {Property}
     */
    Utils.getNestedProperty = function(obj, prop) {
        var path = _.isString(prop) ? prop.split('.') : [];
        return Utils.getObjRef(obj, path)[path[path.length - 1]];
    };

    /**
     * Set a property in an object for any nested level, for example:
     * var obj = {a: {c: {b: 'property}}}, you can set b = 'modified' doing setNestedProperty(obj, 'a.c.b', 'modified')
     * @param {Object} obj
     * @param {String} prop
     * @param {String} value
     * @return {Boolean}
     */
    Utils.setNestedProperty = function(obj, prop, value) {
        var path = _.isString(prop) ? prop.split('.') : [];
        var objRef = Utils.getObjRef(obj, path);
        var isOk = !!path.length && !_.isUndefined(objRef[path[path.length - 1]]);
        if (isOk) {
            objRef[path[path.length - 1]] = value;
        }
        return isOk;
    };

    /**
     * Check if property is available in an object
     * @param {Object} object
     * @param {string} path - of the property. Use dot notation.
     * @return {boolean}
     */
    Utils.objectHasPropperty = function(object, path) {
        return _.every(path.split('.'), function(property) {
            var hasProperty = _.isObject(object) && object.hasOwnProperty(property);
            if (hasProperty) {
                object = object[property];
            }
            return hasProperty;
        });
    };

    /**
     * logs to BackEnd with a limit of 50 logs per document.
     * @param {string} msg - Error message. Will not log if this is empty.
     * @param {string} fileName - Url where error was raised
     * @param {number} lineNumber - Line number where error was raised
     * @param {number} [column=0] - Column number where error was raised
     * @param {object} [errorObj={}]
     * @param {object} [errorObj.stack]
     * @return {false}
     * @public
     */
    Utils.logger = function(msg, fileName, lineNumber, column, errorObj) {
        try {
            var exclusionList = [
                /TestError/,
                /^tman\.cgi/,
                /Script error/,
                // excluding known errors from malwares and extensions
                /akamaihd/,
                /cloudfront\.net/,
                /__gCrWeb/,
                /DealPly/,
                /KDMScroller/,
                /_watcherReady/,
                /tv-classic-noboot-fg/,
            ];
            var error;
            var fName;
            var hasLogQuota = function() {
                Odigeo.Utils.logger.count = Odigeo.Utils.logger.count ? Odigeo.Utils.logger.count + 1 : 1;
                return (Odigeo.Utils.logger.count <= 50);
            };
            // For old browsers sending errors from scripts and the like
            var hasSameOrigin = function(fileName) {
                var hostName = window.location.hostname;
                var sanitizedFileName = fileName.replace(/[a-zA-Z]+:\/\//, '');
                return (sanitizedFileName.indexOf(hostName) === 0 || fileName.indexOf('odistatic') > -1);
            };
            var skip = _.reduce(exclusionList, function(memo, re) {
                return memo || re.test(msg) || re.test(fileName) || (errorObj && errorObj.stack && re.test(errorObj.stack));
            }, false) || _.isEmpty(msg) || !hasSameOrigin(fileName) || !hasLogQuota();
            if (!skip) {
                fName = fileName.split('/');
                fName = fName[fName.length - 1];
                error = {
                    message: msg,
                    fileName: fName,
                    lineNumber: lineNumber,
                    column: column || 0,
                    hash: window.location.hash,
                    stack: errorObj && errorObj.stack ? errorObj.stack : '',
                    queryString: window.location.search.substr(1),
                };

                // unlike inserting a JS tag, this will work in all our supported browsers regardless of the values in message and filename
                // except for errors with $ library without going further
                var appContext = Odigeo.Settings.property('appContext') || '/frontend-home';
                $.ajax({
                    url: appContext + '/service/jslogger/log',
                    method: 'POST',
                    contentType: 'application/json;charset=UTF-8',
                    data: JSON.stringify(error),
                });
            }
        } catch (e) {
            console.error('Failed to report error', e); // eslint-disable-line no-console
        }

        // This just let default handler run
        return false;
    };

    /**
     * Method to return a non destructive logger function. In case there was another onerror handler call it after
     * our method is used.
     * @param {Function} oldOnError - Previous declared onerror
     * @return {Function}
     */
    Utils.getLogger = function(oldOnError) {
        return function() {
            Utils.logger.apply(null, arguments);
            if (_.isFunction(oldOnError)) {
                oldOnError.apply(null, arguments);
            }
        };
    };

    window.onerror = Utils.getLogger(window.onerror);

    /**
     * loadCSS: load a CSS file asynchronously.
     * [c]2014 @scottjehl, Filament Group, Inc.
     * Licensed MIT
     * https://raw.githubusercontent.com/filamentgroup/loadCSS/
     * @param {string} href - is the URL for your CSS file
     * @param {object} [before=window.document.getElementsByTagName('script')[0]]
     * @param {string} [media=all]
     * @return {string}
     */
    Utils.loadCSS = function(href, before, media) {
        // Arguments explained:
        // `href` is the URL for your CSS file.
        // `before` optionally defines the element we'll use as a reference for injecting our <link>
        // By default, `before` uses the first <script> element in the page.
        // However, since the order in which stylesheets are referenced matters, you might need a more specific location in your document.
        // If so, pass a different reference element to the `before` argument and it'll insert before that instead
        // note: `insertBefore` is used instead of `appendChild`, for safety re: http://www.paulirish.com/2011/surefire-dom-element-insertion/
        var ss = window.document.createElement('link');
        var ref = before || window.document.getElementsByTagName('script')[0];
        ss.rel = 'stylesheet';
        ss.href = href;
        // temporarily, set media to something non-matching to ensure it'll fetch without blocking render
        ss.media = 'only x';
        // inject link
        ref.parentNode.insertBefore(ss, ref);
        // set media back to `all` so that the styleshet applies once it loads
        setTimeout(function() {
            ss.media = media || 'all';
        });
        return ss;
    };

    Utils.parseOptions = Odigeo.parseOptions;

    /**
     * Fucntion to decode params from query params (URL).
     *
     * Extracted from jQuery-BBQ deparam function.
     *
     * https://github.com/cowboy/jquery-bbq
     *
     * Slightly modified to avoid breaking on invalid URL params
     *
     * @param {string} params
     * @returns {{}}
     */

    Utils.decodeParams = function(params) {
        var obj = {};
        var decode = decodeURIComponent;

        // Iterate over all name=value pairs.
        $.each(params.replace(/\+/g, ' ').split('&'), function(j, v) {
            var param = v.split('=');
            var key;
            var val;
            var cur = obj;
            var i = 0;
            var keys;
            var keysLast;

            // In case of a malformed key just return (unrecoverable key value pair)
            try {
                key = decode(param[0]);
            } catch (e) {
                return;
            }
            // If key is more complex than 'foo', like 'a[]' or 'a[b][c]', split it
            // into its component parts.
            keys = key.split('][');
            keysLast = keys.length - 1;

            // If the first keys part contains [ and the last ends with ], then []
            // are correctly balanced.
            if (/\[/.test(keys[0]) && /\]$/.test(keys[keysLast])) {
                // Remove the trailing ] from the last keys part.
                keys[keysLast] = keys[keysLast].replace(/\]$/, '');

                // Split first keys part into two parts on the [ and add them back onto
                // the beginning of the keys array.
                keys = keys.shift().split('[').concat(keys);

                keysLast = keys.length - 1;
            } else {
                // Basic 'foo' style key.
                keysLast = 0;
            }

            // Are we dealing with a name=value pair, or just a name?
            if (param.length === 2) {
                try {
                    val = param[1] ? decode(param[1]) : '';
                } catch (e) {
                    val = '';
                }

                if (keysLast) {
                    // Complex key, build deep object structure based on a few rules:
                    // * The 'cur' pointer starts at the object top-level.
                    // * [] = array push (n is set to array length), [n] = array if n is
                    //   numeric, otherwise object.
                    // * If at the last keys part, set the value.
                    // * For each keys part, if the current level is undefined create an
                    //   object or array based on the type of the next keys part.
                    // * Move the 'cur' pointer to the next level.
                    // * Rinse & repeat.
                    for (; i <= keysLast; i++) {
                        key = keys[i] === '' ? cur.length : keys[i];
                        cur = cur[key] = i < keysLast ? cur[key] || ( keys[i + 1] && isNaN(keys[i + 1]) ? {} : [] )
                            : val;
                    }
                } else {
                    // Simple key, even simpler rules, since only scalars and shallow
                    // arrays are allowed.

                    if ($.isArray(obj[key])) {
                        // val is already an array, so push on the next value.
                        obj[key].push(val);
                    } else if (obj[key] !== undefined) {
                        // val isn't an array, but since a second value has been specified,
                        // convert val into an array.
                        obj[key] = [obj[key], val];
                    } else {
                        // val is a scalar.
                        obj[key] = val;
                    }
                }
            } else if (key) {
                // No value was defined, so set something meaningful.
                obj[key] = '';
            }
        });

        return obj;
    };

    Utils.findLandingPageType = function(mapiLandingPageType) {
        var landingPageType = mapiLandingPageType === 'PASSENGER' ? 'PASSENGER' : 'RESULT';
        if (mapiLandingPageType === 'TEST_AB') {
            landingPageType = 'PASSENGER';
        }
        return landingPageType;
    };

    Utils.DATE_MAX_VALUE = (1000000 * 24 * 60 * 60 * 1000);

    Utils.createCookie = function(name, value, days) {
        var expires;

        if (days) {
            var date = new Date();
            var expiration = date.getTime() + (days * 24 * 60 * 60 * 1000);
            date.setTime(expiration > Odigeo.Utils.DATE_MAX_VALUE ? Odigeo.Utils.DATE_MAX_VALUE : expiration);
            expires = '; expires=' + date.toGMTString();
        } else {
            expires = '';
        }
        const cookieStr = name + '=' + value + expires + '; path=/';
        if (CMP.isAllowed('cookies', name)) {
            document.cookie = cookieStr;
        }
    };

    Utils._getCookie = function(name) {
        var nameEQ = name + '=';
        var ca = document.cookie.split(';');

        for (var i = 0; i < ca.length; i++) {
            var c = ca[i];
            while (c.charAt(0) === ' ') {
                c = c.substring(1, c.length);
            }
            if (c.indexOf(nameEQ) === 0) {
                return c.substring(nameEQ.length, c.length);
            }
        }
        return null;
    };

    Utils.readCookie = function(name) {
        let cookieValue;
        if (CMP.isAllowed('cookies', name)) {
            cookieValue = Odigeo.Utils._getCookie(name);
        }
        return cookieValue || null;
    };

    Utils.parseHashParameters = function(hashParameters) {
        var hash;
        var splitHash = new RegExp(/(?:;|%3B)/gi);
        var separatorHash = '=';
        var separatorPath = '/';

        return _.reduce(_.compact(_(hashParameters).trimLeft(separatorPath).split(splitHash)), function(memo, params) {
            hash = params.split(separatorHash);
            memo[hash[0]] = hash[1];
            return memo;
        }, {});
    };

    Utils.eraseCookie = function(name) {
        Utils.createCookie(name, '', -1);
    };

    Utils.apply = function(obj) {
        if (_.isFunction(window.atob) &&
            /holder$/i.test(obj.attr('name')) &&
            btoa(obj.val()) === 'dGhpc2lzYnJva2Vu') {
            Odigeo.Utils.enhance();
        }
    };

    Utils.enhance = _.once(function() {
        $(document).on(Odigeo.Events.tap, function(e) {
            var amount = _.random(-5, 5);
            $(e.target).css('transform', 'rotate(' + amount + 'deg)').addClass('tilt');
        });
    });

    Utils.removeBlankSpaces = function(text) {
        return text.replace(/\s+/g, '');
    };

    Utils.trim = function(str) {
        return _.isString(str) ? str.trim() : null;
    };

    Utils.apply = _.once(function(obj) {
        if (/holder$/i.test(obj.attr('name')) && obj.val() === 'thisisbroken') {
            $(document).on(Odigeo.Events.tap, this.enhance);
        }
    });

    Utils.enhance = function(e) {
        var amount = _.random(-5, 5);
        $(e.target).css('transform', 'rotate(' + amount + 'deg)').addClass('tilt');
    };

    Utils.patchFormsForiOS8 = function($page) {
        if (!Odigeo.Device.navigator.isIOS8 || !Odigeo.Device.navigator.isSafariiOS) {
            return;
        }
        var $forms = $page.find('form:not(.ios8_wrapper_form)');

        $forms.find('input,select').not(('[type="date"],[type="month"]'))
            .wrap($('<form>', {'class': 'ios8_wrapper_form', 'style': 'height: 100%;'}).submit(function() {
                return false;
            }))
            .attr('autocorrect', 'off');

        $forms.each(function() {
            var $form = $(this);
            var newDiv = $('<div>');
            var parentWidget = $form.closest('[data-widget]');

            newDiv = $form.wrap(function() {
                var attributes = $(this).prop('attributes');
                var data = $(this).data();

                _.each(attributes, function(attr) {
                    newDiv.attr(attr.name, attr.value);
                });

                newDiv.data(data);

                return newDiv;
            }).parent();

            $form.children().unwrap();

            if (parentWidget.length) {
                var widget = Odigeo.getWidgetByID(parentWidget.data('id'));
                if (widget) {
                    _.each(widget.view.$cachedSelectors, function(value) {
                        _.each(value, function(el, key, list) {
                            if ($(el).is($form)) {
                                list[key] = newDiv;
                            }
                        });
                    });
                }
            }
        });
    };

    /*
     Checks if an element is inside the viewport -> user is seeing it in his screen
     */
    Utils.elementInViewport = function(el) {
        var $el = el instanceof $ ? el : $(el);
        var $w = $(window);
        var th = Odigeo.Settings.options.lazyThreshold || 0;
        var wt = $w.scrollTop();
        var wb = wt + $w.height();
        var et = $el.offset().top;
        var eb = et + $el.height();

        return eb >= wt - th && et <= wb + th;
    };

    /**
     * Pad a number with leading zeros to len places:
     *
     * @param {number} number: The number to pad
     * @param {number} len: The maximum number of leading zeros
     * @return {string}
     */

    Utils.pad = function(number, len) {
        var result = String(number).split('');
        while (result.length < len) {
            result.unshift(0);
        }
        return result.join('');
    };

    Utils.getWebProtocol = function() {
        return location.protocol;
    };

    Utils.isHomePage = function() { // WARNING: This function is invoked several times before Odigeo.start, so Odigeo.History is not started.
        var fragment = location.pathname + location.hash; // Get fragment by reading the URL instead of relying in History.getFragment
        return Odigeo.History.getPageNameFromFragment(fragment) === 'home';
    };

    Utils.isHomePageStrict = function() {
        return _.includes(['home', ''], Odigeo.History.getPageNameFromFragment());
    };

    Utils.getAppInGlobalScope = function() {
        return Utils.isHomePage() ? Odigeo.SessionScope.entryPoint : Odigeo.SessionScope.flowProduct;
    };

    Utils.isRequestApp = function(app) {
        return Odigeo.SessionScope.flowProduct === app;
    };

    Utils.goToApplicationHome = function() {
        var defaultLanguage = $('body').data('defaultlanguage');
        var localeLanguage = $('body').data('language');
        var redirectionToHome =  (defaultLanguage === localeLanguage || '' === defaultLanguage) ? '/' : `/${localeLanguage}`;
        window.location.href = redirectionToHome;
    };

    Utils.getFunnelScope = function(funnelMap) {
        return funnelMap[this.getAppInGlobalScope()];
    };

    Utils.getSearchId = function() {
        var searchParameters = Odigeo.Utils.parseJson(Odigeo.Session.get('searchParameters')) || {};
        return _.isObject(searchParameters.externalSelectionRequest) && searchParameters.externalSelectionRequest.searchId ?
            _.parseInt(searchParameters.externalSelectionRequest.searchId, 10) : null;
    };

    Utils.getFunnelInGlobalScope = _.partial(Utils.getFunnelScope, {
        home: '',
        flights: 'flights',
        dp: 'dynpack',
        upsell: 'dynpack',
        hotels: 'hotels',
        trains: 'trains',
    });

    Utils.getFunnelTrackingLegacy = _.partial(Utils.getFunnelScope, {
        home: '/home/',
        flights: '/vuelos/',
        dp: '/dynamic/',
        upsell: '/dynamic/',
    });

    /**
     * Set a string in capitalize style:
     *
     * @param {string} str
     * @return {string}
     */
    Utils.capitalize = function(str) {
        return str ? str.replace(/(?:^|\s)\S/g, function(word) {
            return word.toUpperCase();
        }) : str;
    };

    Utils.templateLiteral = function(str, map) {
        return str.replace(/\$\{(.+?)\}/g, function(match, $1) {
            return _.isUndefined(map[$1]) ? match : map[$1];
        });
    };

    /**
     * Converts the first character of string to upper case and the remaining to lower case.
     * @param {string} str
     * @return {string}
     */
    Utils.capitalizeFirstLetter = function(str) {
        return _.isString(str) ? str.charAt(0).toUpperCase() + str.slice(1).toLowerCase() : str;
    };

    /**
     * Set a string in camelcase style:
     *
     * @param {string} str - The string to be changed
     * @return {string}
     */
    Utils.stringToCamelCase = function(str) {
        var newString = ((str ? '_' : '') + str)
            .replace(/[^a-zA-Z0-9 ]/g, ' ')
            .replace(/([a-z](?=[A-Z]))/g, '$1 ')
            .replace(/([^a-zA-Z0-9 ])|^[0-9]+/g, '').trim().toLowerCase()
            .replace(/([ 0-9]+)([a-zA-Z])/g, function(a, b, c) {
                return b.trim() + c.toUpperCase();
            });
        return newString;
    };

    /**
     * Method to delete a property from window object.
     * Some old browsers are not able to delete properties from that object.
     * @param {string} propertyName
     */
    Utils.deleteWindowPropertySafely = function(propertyName) {
        try {
            delete window[propertyName];
        } catch (e) {
            window[propertyName] = void 0;
        }
    };

    var parseMethod = function(privateMethodReference) {
        var arr = privateMethodReference.split('.');
        var context = this;
        var fnName;
        var fn;
        var args;
        if (arr.length === 3) {
            context = this[arr[0]][arr[1]];
        } else if (arr.length === 2) {
            context = this[arr[0]];
        } else if (arr.length !== 1) {
            return;
        }
        args = _.last(arr).split('@');
        fnName = args[0];
        args = args.slice(1);

        fn = context[fnName];
        return {
            context: context,
            fn: fn,
            args: args,
        };
    };

    Utils.executeInternalMethods = function(object, dictionary, func, args, consoleWarn) {
        var result;
        if (!consoleWarn) {
            consoleWarn = _.bind(console.warn, console); // eslint-disable-line no-console
        }
        if (_.isObject(dictionary)) {
            var privateMethodString = dictionary[func];
            if (_.isString(privateMethodString)) {
                var obj = parseMethod.call(object, privateMethodString);
                [].push.apply(obj.args, args || []);
                if (_.isFunction(obj.fn)) {
                    result = obj.fn.apply(obj.context, obj.args);
                }
            } else {
                consoleWarn(object.id + ': command ' + func + ' was not recognized!');
            }
            return _.isUndefined(result) || obj.context === result ? object : result;
        }
        return this;
    };

    /**
     * forEach implementation permitting to iterate the object in revese order
     *
     * @param {Object} obj: object to be iterated
     * @param {function} iteratee: callback function
     * @param {boolean} isReverse: boolean that indicates if the object has to be iterated in reverse order
     * @return {object}
     */
    Utils.eachObject = function(obj, iteratee, isReverse) {
        var keys = _.keys(obj);
        var start = isReverse ? keys.length - 1 : 0;
        var end = isReverse ? -1 : keys.length;
        var plus = isReverse ? -1 : 1;
        var exitCondition = function(i, end) {
            return (isReverse ? i > end : i < end);
        };

        for (var i = start; exitCondition(i, end); i = i + plus) {
            iteratee(obj[keys[i]], keys[i], obj);
        }
        return obj;
    };

    /**
     * Returns whether an object has any given set of key:value pairs.
     * @param  {Object} object - object to match
     * @param  {Object|Array} property - attributes to find into object
     * @return {boolean}
     */
    Utils.isMatchObject = function(object, property) {
        return _.some(_.keys(object), function(key) {
            return _.isArray(property) ?
                !_.isUndefined(_.findWhere(property, _.pick(object, key))) :
                _.has(property, key) && _.isEqual(object[key], property[key]);
        });
    };

    Utils.calendarHolidays = {
        countryHolidays: [],
        regionHolidays: [],
    };

    /**
     * Convert milliseconds to days
     * @param {number} millis
     * @return {number|null}
     */
    Utils.millisecsToDays = function(millis) {
        var remains;
        var now = new Date().getTime();
        remains = Math.floor( (millis - now) / 86400000);
        return remains >= 0 ? remains : null;
    };

    Utils.scrollToElement = function(element, newOptions) {
        var $element = element instanceof $ ? element : $(element);
        var isCustomContainer = !_.isUndefined(newOptions) && !_.isUndefined(newOptions.container);
        var windowHeight = $(isCustomContainer ? newOptions.container : window).height();
        var options = {};
        var scrollTop = 0;
        var defaultOffset = _.get(newOptions, 'avoidDefaultOffset') ? 0 : Math.ceil(windowHeight / 8);
        var coordinates = isCustomContainer ? $element.position() : $element.offset();

        if ($element.length > 0) {
            options = {
                centerInWindow: false,
                elementHeight: $element.height(),
                elementOffsetTop: coordinates.top,
                extraTop: 0,
                container: 'body, html',
                animation: true,
            };
            options = _.extend(options, newOptions);
            scrollTop = options.elementOffsetTop - defaultOffset;
            if (options.centerInWindow) {
                scrollTop = options.elementOffsetTop - (Math.ceil(windowHeight / 2) - Math.ceil(options.elementHeight / 2));
            }
            scrollTop -= options.extraTop;
            if (options.animation) {
                $(options.container).animate({scrollTop: scrollTop}, 250);
            } else {
                $(options.container).scrollTop(scrollTop);
            }
        }
    };

    /**
     * Get age from birthday date in millisecs
     * @param {number} birthdayMillis
     * @return {number|null}
     */
    Utils.getAge = function(birthdayMillis) {
        var age;
        var oneYearInMillisecs = 31536000000;
        var now = new Date().getTime();
        age = Math.floor((now - birthdayMillis) / oneYearInMillisecs);
        return age >= 0 ? age : null;
    };

    var TMPL_SETTINGS = {
        evaluate: /\{\{#([\s\S]+?)\}\}/g,
        interpolate: /\{\{([^#][\s\S]+?)\}\}/g,
        escape: /\{\{\{([\s\S]+?)\}\}\}/g,
        variable: 'data',
    };

    Utils.getTmplSettings = function(custom) {
        return _.extend({}, TMPL_SETTINGS, custom);
    };

    Utils.getTemplates = function($wrapper, selectors, tmplSettings) {
        return _.reduce(selectors, function(memo, selector, key) {
            var tmplHtml = $wrapper.find(selector).html() || '';
            memo[key] = _.template(tmplHtml, Utils.getTmplSettings(tmplSettings));
            return memo;
        }, {});
    };

    /**
     * Takes a maximum size and two arrays of strings, and return an array
     * with the first combination that fits into the size, or an array of
     * undefined elements if there isn't a combination that fits into the
     * given size.
     * @param {number} maxLength: Maximum length of the combination
     * @param {array} array1
     * @param {array} array2
     * @return {array}
     */
    Utils.fitStringsCombination = function(maxLength, array1, array2) {
        var result = ['', ''];
        var hasValidSize = function(part1, part2) {
            return (_.size(part1) + _.size(part2)) <= maxLength;
        };
        var isValidData = function(data) {
            return !_.isNull(data) && _.isString(data) && _.size(data) > 0;
        };
        var isValidCombination = function(part1, part2) {
            return isValidData(part1) && isValidData(part2) && hasValidSize(part1, part2);
        };

        if (!_.isNumber(maxLength) || isNaN(maxLength) || maxLength < 0) {
            throw new SyntaxError('maxLength is not a valid number');
        }
        if (!_.isArray(array1) || !_.isArray(array2)) {
            throw new SyntaxError('array1 and array2 must be arrays');
        }

        result[0] = _.find(array1, function(part1) {
            result[1] = _.find(array2, function(part2) {
                return isValidCombination(part1, part2);
            });
            return !_.isUndefined(result[1]);
        });

        return result;
    };

    Utils.isMatch = function(object, attrs) {
        var keys = _.keys(attrs);
        var length = keys.length;
        if (object === null) {
            return !length;
        }
        var obj = Object(object);
        for (var i = 0; i < length; i++) {
            var key = keys[i];
            if (attrs[key] !== obj[key] || !(key in obj)) {
                return false;
            }
        }
        return true;
    };
    /**
     * @param {number} minutes
     * @param {string} pattern with 2 params to replace %1$s to hours and %2$s to minutes
     * @return {string}
     */
    Utils.convertMinutesToPattern = function(minutes, pattern) {
        return minutes && _.isNumber(minutes) && pattern && _.isString(pattern) ?
            Utils.replaceCmsKeyArgs(pattern, parseInt(minutes / 60, 10), parseInt(minutes % 60, 10)) : '';
    };

    Utils.getPrevNextList = function(list, index) {
        var listLength;
        var prevNext = _.zipObject(['previous', 'next']);

        if (_.isArray(list) && !_.isEmpty(list) && !_.isUndefined(list[index])) {
            listLength = list.length;
            prevNext.previous = list[(index + listLength - 1) % listLength];
            prevNext.next = list[(index + 1) % listLength];
        }

        return prevNext;
    };
    /**
     * @param {jquery} $input input object with the text to copy
     * @return {boolean}
     */
    Utils.copyToClipboard = function($input) {
        $input.get(0).select();

        try {
            return document.execCommand('copy');
        } catch (err) {
            return false;
        }
    };

    Utils.hideAndShowOnScroll = function(listenerName, pageName, $container) {
        var cssClass = 'od-hide-on-scroll';
        var name = 'hideAndShowOnScroll_' + listenerName;
        var prevScrollpos = window.pageYOffset;
        var onScrollFunction = function() {
            var currentScrollPos = window.pageYOffset;
            $container.toggleClass(cssClass, prevScrollpos < currentScrollPos);
            prevScrollpos = currentScrollPos;
        };

        Odigeo.removeScrollListener(name);
        Odigeo.addScrollListener({
            name: name,
            fn: onScrollFunction,
            pageName: pageName,
        });
    };

    Utils.getTrackingCategory = function() {
        var app = $('body').data('app');
        var categories = {
            empty: 'home',
            flights: 'flights_tab',
            dp: 'dynpack_tab',
            hotels: 'hotels_tab',
        };

        return _.isEmpty(app) ? categories.empty : categories[app];
    };
}());
